/**
 * LocationButton — small pill/round-icon button that lets the user
 * share their EXACT GPS coordinates with Rammy for the current chat
 * session.
 *
 * Behavior
 * --------
 * • Idle: shows a MapPin icon in a subtle border. Tapping fires the
 *   browser Geolocation API.
 * • Requesting: swap to a spinner.
 * • Granted: pill highlights in Rammy cyan, tooltip shows "sharing your
 *   exact location · tap to stop". Tapping clears the stored coords
 *   and reverts to idle.
 * • Denied / error: red-tinted pin, tooltip explains + suggests the
 *   browser settings path.
 *
 * All actual header injection happens in the hooks; this button is a
 * thin visual wrapper.
 */
import React from "react";
import { toast } from "sonner";
import * as L from "lucide-react";
import useExactLocation from "@/hooks/useExactLocation";

export default function LocationButton({
  className = "",
  size = "md",           // "md" for chat composer, "sm" for compact places
  testIdPrefix = "location",
}) {
  const { status, coords, request, clear } = useExactLocation();

  const active = status === "granted" && coords;
  const busy = status === "requesting";
  const denied = status === "denied";

  const handleClick = async () => {
    if (busy) return;
    if (active) {
      clear();
      toast("Exact location off — I'll use approximate.", { icon: "📍" });
      return;
    }
    const got = await request();
    if (got) {
      toast.success("Exact location shared with Rammy");
    } else if (status === "denied" || denied) {
      toast.error("Location blocked. Enable it in your browser settings.");
    } else {
      toast.error("Couldn't fetch your location. Try again.");
    }
  };

  const label = active
    ? "Exact location on — tap to stop sharing"
    : denied
      ? "Location blocked — enable in browser settings"
      : "Share your exact location with Rammy";

  const dim = size === "sm" ? "w-9 h-9" : "w-11 h-11";

  const base =
    "rounded-full flex items-center justify-center shrink-0 transition disabled:opacity-40 " +
    dim;
  const state = active
    ? "border border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] hover:bg-[var(--rammy-cyan)]/30"
    : denied
      ? "border border-red-300 bg-red-50 text-red-500 hover:bg-red-100"
      : "border border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)] text-[var(--rammy-text-sec)]";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`${base} ${state} ${className}`}
      aria-label={label}
      title={label}
      data-testid={`${testIdPrefix}-btn`}
      data-active={active ? "true" : "false"}
      data-status={status}
    >
      {busy ? (
        <L.Loader2 size={16} className="animate-spin"/>
      ) : denied ? (
        <L.MapPinOff size={16}/>
      ) : active ? (
        <L.MapPinCheck size={16}/>
      ) : (
        <L.MapPin size={16}/>
      )}
    </button>
  );
}
